<template>
  <div class="d-flex align-items-center gap-2 flex-wrap">
    <ForwardsStatusBadgeComponent
      v-if="isPayAsProduced"
      status="pay_as_produced"
    />
    <ForwardsStatusBadgeComponent :status="status" />

    <span
      v-if="openVolumeMwh > 0"
      class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
    >
      {{
        $t("forwards.common.deal_badges.under_committed", {
          mwh: formatNumberWithSpaces(openVolumeMwh),
        })
      }}
    </span>

    <span
      v-if="escrowShortfallMwh > 0"
      class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
    >
      {{
        $t("forwards.deals.badges.escrow_shortfall", {
          mwh: formatNumberWithSpaces(escrowShortfallMwh),
        })
      }}
    </span>

    <span
      v-for="flag in flags.infeasibility"
      :key="`${flag.pool_id}-${flag.period_label}`"
      class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
    >
      {{
        $t("forwards.deals.badges.infeasible", {
          pool: flag.pool_name,
          period: flag.period_label,
          mwh: formatNumberWithSpaces(flag.shortfall_mwh),
        })
      }}
    </span>

    <span
      v-if="volumeDriftMwh !== 0"
      class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
    >
      {{ volumeDriftLabel(t, volumeDriftMwh) }}
    </span>

    <span
      v-if="flags.is_stale_draft"
      class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis"
    >
      {{ $t("forwards.deals.badges.stale_draft") }}
    </span>

    <InfoTooltip v-if="driftReportCount > 0" :text="driftReportDetail">
      <span class="badge rounded-pill bg-warning-subtle text-warning-emphasis">
        {{
          $t("forwards.deals.badges.drift_report", { count: driftReportCount })
        }}
      </span>
    </InfoTooltip>

    <span
      v-if="flags.free_escrow_mwh > 0"
      class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
    >
      {{
        $t("forwards.deals.badges.free_escrow", {
          mwh: formatNumberWithSpaces(flags.free_escrow_mwh),
        })
      }}
    </span>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import ForwardsStatusBadgeComponent from "@/forwards2/components/ForwardsStatusBadgeComponent.vue";
import { volumeDriftLabel } from "@/forwards2/deals/dealBadges";
import type { DealFlags, DealStatus } from "@/forwards2/forwardsTypes";
import { sum, values } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The state of a deal at a glance.
 *
 * Every badge is a state, not an error — none of them stops the page rendering, and none of them
 * is a blocking reason on its own (the blocking reasons live on the buttons they disable).
 */
const props = defineProps<{
  status: DealStatus;
  flags: DealFlags;
  isPayAsProduced: boolean;
}>();

const { t } = useI18n();

const openVolumeMwh = computed(() => props.flags.open_volume_mwh);

/** One badge for the whole deal: the per-delivery split is on the delivery cards. */
const escrowShortfallMwh = computed(() =>
  sum(values(props.flags.escrow_shortfall_by_delivery))
);

const volumeDriftMwh = computed(() => props.flags.volume_drift_mwh ?? 0);

const driftReportCount = computed(() => props.flags.drift_report.length);
const driftReportDetail = computed(() =>
  props.flags.drift_report
    .map((entry) =>
      t("forwards.deals.badges.drift_report_line", {
        device:
          entry.device_name ??
          t("forwards.deals.badges.device_number", { id: entry.device_id }),
        reasons: entry.failures.map((failure) => failure.detail).join("; "),
      })
    )
    .join("\n")
);
</script>
